import { Moon, Footprints, Flame, Users, Gauge, MapPin, Clock, Bell } from "lucide-react";

/* One entry per PacSana panel: which tab it is, how it plots, the guidance PacSana
   shows under its own charts, and the amber/red bands those charts draw.

   `keys` lists the reading keys the backend has used for the same measure — the
   first one present wins — so a rename on the API side doesn't blank a tab.

   Accents and band colours are PacSana's brand values, not derived ones. They
   don't clear a generic contrast/CVD audit; what carries the weight instead is
   that only one reading is ever plotted at a time and every band, bar and tab is
   labelled in text, so nothing here is distinguished by colour alone. */

export const BAND_COLORS = {
  green: "#12b886",
  amber: "#f0a13c",
  red: "#ef5b6b",
};

export const READINGS = [
  {
    id: "activity_24h",
    keys: [],
    label: "24-Hour Activity",
    Icon: Clock,
    chart: "clock",
    accent: "violet",
    decimals: 1,
    why: [
      "This panel shows when and where activity has taken place over the selected 24 hour period.",
      "Accuracy depends on having full signal coverage. If the bracelet is not being detected in some areas, consider adding more gateways.",
    ],
    what: [
      "Check for bed to living area transitions and step activity at meal times as positive signs that all is well.",
    ],
  },
  {
    id: "sleep",
    keys: ["sleep", "sleep_history", "sleepHistory"],
    label: "Sleep history",
    Icon: Moon,
    chart: "bar",
    accent: "slate",
    unit: "hours",
    decimals: 1,
    bands: [
      { value: 6, tone: "amber", label: "Good Sleep" },
      { value: 5, tone: "red", label: "Low Sleep" },
    ],
    verdict: (avg) => {
      if (avg >= 7 && avg <= 8) return "which is ideal";
      if (avg >= 6 && avg <= 9) return "which is good";
      if (avg > 9) return "which is more than is typically needed";
      if (avg >= 5) return "which is below the 6-hour mark";
      return "which is well below the 6-hour mark";
    },
    why: [
      "Sleep acts as the brain's housekeeper, facilitating the removal of metabolic waste to keep us mentally strong and healthy. Poor sleep can cause short-term cognitive impairment and long-term decline. Good sleep, in contrast, increases older adults' ability to perform activities of daily living, reduces fall risk and hospitalization.",
      "7 to 8 hours of sleep is ideal. 6 to 9 hours is also good.",
    ],
    what: ["These changes can be used to improve sleep:"],
    whatList: [
      "Have a regular bed time.",
      "Exercise daily.",
      "Avoid daytime naps.",
      "Avoid alcohol.",
      "Keep the bedroom dark and cool at night.",
      "Wind down near bedtime.",
    ],
    whatAfter: [
      "Persistent poor sleep, either insufficient or excessive, should be discussed with a medical practitioner.",
    ],
  },
  {
    id: "active_minutes",
    keys: ["active_minutes", "activeMinutes"],
    label: "Active minutes",
    Icon: Footprints,
    chart: "line",
    accent: "green",
    unit: "mins",
    decimals: 0,
    bands: [
      { value: 40, tone: "amber", label: "Daily Living Typical" },
      { value: 25, tone: "red", label: "Daily Living Minimum" },
    ],
    verdict: (avg) => {
      if (avg >= 40) return "which is well above what is typically needed for self care";
      if (avg >= 25) return "which is around what is typically needed for self care";
      return "which is below the minimum needed for daily living activities";
    },
    why: [
      "This is walking minutes per day, both inside and outside the home. Since movement is needed for self-care, it indicates whether care support might be needed.",
    ],
    what: [
      "If below the amber value, a person may not have moved enough to have completed Activities of Daily Living such as dressing and washing. Red implies a likely need for care support with those activities.",
    ],
  },
  {
    id: "exercise",
    keys: ["exercise", "exercise_minutes", "exerciseMinutes"],
    label: "Exercise minutes",
    Icon: Flame,
    chart: "bar",
    accent: "orange",
    unit: "mins",
    decimals: 0,
    bands: [{ value: 30, tone: "green", label: "WHO Recommendation" }],
    verdict: (avg) =>
      avg >= 30
        ? "which meets the 30-minute World Health Organization guideline"
        : "which is below the 30-minute World Health Organization guideline",
    why: [
      "This is the time spent doing moderate to vigorous exercise per day, enough to get slightly out of breath. It is measured inside and outside the home.",
      "The World Health Organisation recommends 150 minutes per week, or 30 minutes per day for adults over 65. Strength and balance exercises are also recommended to enhance functional capacity and to prevent falls.",
    ],
    what: [
      "Most older adults don't achieve the WHO target. However, if able, any increase in exercise participation can improve sleep and many other health conditions, including cognitive ability.",
    ],
  },
  {
    id: "gait_speed",
    keys: ["gait_speed", "gaitSpeed"],
    label: "Gait speed",
    Icon: Gauge,
    chart: "line",
    accent: "blue",
    unit: "steps/min",
    decimals: 0,
    bands: [
      { value: 86, tone: "green", label: "Good Speed" },
      { value: 50, tone: "amber", label: "Slow" },
      { value: 40, tone: "red", label: "Very Slow" },
    ],
    verdict: (avg) => {
      if (avg >= 86) return "which is a good walking speed";
      if (avg >= 50) return "which is sub-optimal";
      if (avg >= 40) return "which is slow";
      return "which is very slow";
    },
    why: [
      "Walking speed is a key indicator of fall risk and their ability to care for themselves. It is measured both inside and outside the home.",
    ],
    what: [
      "If amber or red, this implies an increased fall risk. Caregiver support may be needed when out walking.",
      "Fall risk can be reduced through a strength and balance program. Also, review the home to improve lighting, remove trip hazards and add physical supports.",
    ],
  },
  {
    id: "daily_absence",
    keys: ["daily_absence", "dailyAbsence", "absence", "out_of_home", "away_time"],
    label: "Daily absence",
    Icon: MapPin,
    chart: "bar",
    accent: "steel",
    unit: "hours",
    decimals: 1,
    why: [
      "This is time spent outside the home area each day. Detection depends on having full signal coverage. If the bracelet is not being detected in some areas, consider adding more gateways.",
    ],
    what: [
      "If Daily Absence is limited, consider arranging additional companionship, either in person or on the phone.",
    ],
  },
  {
    id: "events",
    keys: ["events", "eventHistory", "event_history"],
    label: "Event history",
    Icon: Bell,
    chart: "events",
    accent: "amber",
    // A count on the chip would say how many events came back on this page, not
    // how many there are — so this tab carries its icon and name alone.
    hideFigure: true,
    why: [
      "Each entry is something PacSana's own rules raised against this client's readings over the selected range, with the rule that produced it and the state it was left in.",
    ],
    what: [
      "Read an event against the day it falls on: the 24-hour clock and the daily charts show what the bracelet was reporting at the time.",
    ],
  },
  {
    id: "community",
    keys: ["community", "community_time", "communityTime"],
    // Hidden for now — kept here rather than deleted, since the backend still
    // serves the reading and PacSana's own dashboard has no such panel.
    hidden: true,
    label: "Community time",
    Icon: Users,
    chart: "bar",
    accent: "rose",
    unit: "hours",
    decimals: 1,
    why: [
      "This is time spent in shared or communal areas rather than alone in a private space, and is a proxy for how much company the client is keeping.",
    ],
    what: [
      "Sustained low community time can point to social isolation. Consider arranging companionship or group activities.",
    ],
  },
];

/** The readings that get a tab — everything not currently hidden. */
export const VISIBLE_READINGS = READINGS.filter((reading) => !reading.hidden);

/** The reading behind a tab, under whichever key the backend served it. */
export const resolveReading = (readings, config) => {
  if (!readings || !config) return null;
  const key = config.keys.find((candidate) => readings[candidate]);
  return key ? readings[key] : null;
};

/**
 * The events behind the Event history tab, from whichever shape they arrived in:
 * HistoricalData nests them under `events.items`, and a bare list turns up when
 * the backend flattens them.
 *
 * Newest first — an event list is read from the top.
 */
export const readingEvents = (raw) => {
  const list = (Array.isArray(raw) ? raw : raw?.items || raw?.events) || [];
  return list
    .map((item) => ({
      at: item?.date || item?.timestamp || item?.time || null,
      message: item?.message || item?.text || item?.description || "",
      status: item?.status || "",
      ruleType: item?.ruleType || item?.rule || item?.type || "",
    }))
    .filter((event) => event.at || event.message)
    .sort((a, b) => String(b.at || "").localeCompare(String(a.at || "")));
};

/**
 * How many events there are in total, when the endpoint says so.
 *
 * Null when it doesn't — the panel then works it out from the page it was given,
 * rather than claiming a total it was never told.
 */
export const readingEventsTotal = (raw) =>
  raw?.total ??
  raw?.totalRecords ??
  raw?.totalCount ??
  raw?.totalItems ??
  raw?.count ??
  raw?.pagination?.total ??
  raw?.pagination?.totalRecords ??
  raw?.meta?.total ??
  null;

/** The dates a reading carries, whichever of the two shapes it arrived in. */
export const readingPoints = (raw) =>
  (Array.isArray(raw) ? raw : raw?.points) || [];

const toValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

/**
 * One reading in the shape the charts read, from either shape the API serves.
 *
 * PacSana's HistoricalData endpoint returns a bare `[{ date, value }]` array per
 * measure — no unit, no aggregates — while our own summary endpoint wraps each
 * one with `{ unit, average, latest, daysWithData }`. Both land here, so the
 * panel works whether the shaping happens on our backend or not.
 *
 * `days` is the full day axis for the range, so a day the API left out shows as
 * a gap in the chart rather than pulling the neighbouring days together.
 */
export const normalizeReading = (raw, config, days = []) => {
  if (!raw) return null;

  const byDate = new Map();
  readingPoints(raw).forEach((point) => {
    if (point?.date) byDate.set(String(point.date).slice(0, 10), point);
  });

  const axis = days.length ? days : [...byDate.keys()].sort();
  const points = axis.map((date) => {
    const point = byDate.get(date);
    return {
      date,
      value: toValue(point?.value ?? point?.val),
      description: point?.description,
    };
  });

  const recorded = points.filter((point) => point.value !== null);
  const average =
    raw.average ??
    (recorded.length
      ? recorded.reduce((sum, point) => sum + point.value, 0) / recorded.length
      : null);

  return {
    unit: raw.unit || config.unit || "",
    points,
    daysWithData: raw.daysWithData ?? recorded.length,
    average,
    latest: raw.latest ?? recorded[recorded.length - 1] ?? null,
  };
};

export default READINGS;
